import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { Eye, EyeOff, Plus, X, Edit, Trash2, ArrowUpDown, ArrowUp, ArrowDown, Users } from 'lucide-react';
import { PersonNumber } from '../../types';
import { stalkerAPI, getStaticFileUrl } from '../../services/api';
import { useSearch } from '../../contexts/SearchContext';
import { useDarkMode } from '../../contexts/DarkModeContext';
import { STATUS_MAP } from '../../utils/enums';
import loggingService from '../../services/loggingService';

interface NumberWithPerson extends PersonNumber {
	person_name?: string;
	person_surname?: string;
	person_hideHome?: string;
	person_avatar?: string;
}

const Numbers: React.FC = () => {
	const { globalSearchTerm } = useSearch();
	const { isDark } = useDarkMode();
	const navigate = useNavigate();
	const [numbers, setNumbers] = useState<NumberWithPerson[]>([]);
	const [loading, setLoading] = useState(true);
	const [showModal, setShowModal] = useState(false);
	const [showHidden, setShowHidden] = useState(false);
	const [editingNumber, setEditingNumber] = useState<NumberWithPerson | null>(null);
	const [formData, setFormData] = useState({
		pn_personId: '',
		pn_number: '',
		pn_status: 'active'
	});
	const [persons, setPersons] = useState<any[]>([]);
	const [sortField, setSortField] = useState<string | null>(null);
	const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

	const formatPhoneNumber = (phone: string): string => {
		// Remove all non-digit characters
		const digits = phone.replace(/\D/g, '');
		// Format as 0555 555 55 55 (Turkish format)
		if (digits.length === 11 && digits.startsWith('0')) {
			return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7, 9)} ${digits.slice(9)}`;
		} else if (digits.length === 10) {
			return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 8)} ${digits.slice(8)}`;
		}
		// Return original if format doesn't match
		return phone;
	};

	const hasLoggedPageView = useRef(false);

	useEffect(() => {
		loadData();
		loadPersons();
		if (!hasLoggedPageView.current) {
			loggingService.logPageView('/stalker/numbers', 'Stalker', 'Numbers');
			hasLoggedPageView.current = true;
		}
	}, []);

	const loadData = async () => {
		try {
			const response = await stalkerAPI.numbers.getAll();
			setNumbers(response.data.data);
		} catch (error) {
			console.error('Error loading numbers:', error);
		} finally {
			setLoading(false);
		}
	};

	const loadPersons = async () => {
		try {
			const response = await stalkerAPI.persons.getAll();
			setPersons(response.data.data);
		} catch (error) {
			console.error('Error loading persons:', error);
		}
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		try {
			if (editingNumber) {
				await stalkerAPI.numbers.update(editingNumber.pn_id, formData);
				loggingService.logEdit('number', editingNumber.pn_id, formData.pn_number, 'Stalker');
			} else {
				const response = await stalkerAPI.numbers.create({
					...formData,
					pn_personId: Number(formData.pn_personId)
				});
				const numberId = response.data.data?.pn_id;
				if (numberId) {
					loggingService.logCreate('number', numberId, formData.pn_number, 'Stalker');
				}
			}
			setShowModal(false);
			setEditingNumber(null);
			setFormData({ pn_personId: '', pn_number: '', pn_status: 'active' });
			loadData();
		} catch (error) {
			console.error('Error saving number:', error);
		}
	};

	const handleDelete = async (id: number) => {
		if (!window.confirm('Are you sure you want to delete this number?')) return;
		try {
			const number = numbers.find(n => n.pn_id === id);
			await stalkerAPI.numbers.delete(id);
			if (number) {
				loggingService.logDelete('number', id, number.pn_number || 'Unknown', 'Stalker');
			}
			loadData();
		} catch (error) {
			console.error('Error deleting number:', error);
		}
	};

	const filteredNumbers = numbers.filter(number => {
		const matchesSearch = globalSearchTerm === '' ||
			number.pn_number?.toLowerCase().includes(globalSearchTerm.toLowerCase()) ||
			number.person_name?.toLowerCase().includes(globalSearchTerm.toLowerCase()) ||
			number.person_surname?.toLowerCase().includes(globalSearchTerm.toLowerCase());
		const matchesHideFilter = showHidden || number.person_hideHome !== '1';
		return matchesSearch && matchesHideFilter;
	});

	const sortedNumbers = [...filteredNumbers].sort((a, b) => {
		if (!sortField) return 0;
		
		let aValue: any = a[sortField as keyof NumberWithPerson];
		let bValue: any = b[sortField as keyof NumberWithPerson];
		
		// Handle nested person fields
		if (sortField === 'person_name') {
			aValue = a.person_name || '';
			bValue = b.person_name || '';
		} else if (sortField === 'person_surname') {
			aValue = a.person_surname || '';
			bValue = b.person_surname || '';
		}
		
		if (aValue === undefined || aValue === null) aValue = '';
		if (bValue === undefined || bValue === null) bValue = '';
		
		if (typeof aValue === 'string') {
			aValue = aValue.toLowerCase();
			bValue = bValue.toLowerCase();
		}
		
		if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
		if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
		return 0;
	});

	const handleSort = (field: string) => {
		if (sortField === field) {
			if (sortDirection === 'asc') {
				setSortDirection('desc');
			} else if (sortDirection === 'desc') {
				// 3rd click: reset to default
				setSortField(null);
				setSortDirection('asc');
			}
		} else {
			setSortField(field);
			setSortDirection('asc');
		}
	};

	const SortIcon = ({ field }: { field: string }) => {
		if (sortField !== field) {
			return <ArrowUpDown className="w-4 h-4 ml-1" />;
		}
		return sortDirection === 'asc' ? <ArrowUp className="w-4 h-4 ml-1" /> : <ArrowDown className="w-4 h-4 ml-1" />;
	};

	if (loading) {
		return (
			<div className={`flex flex-col items-center justify-center h-64 font-mono text-xs ${isDark ? 'text-emerald-500' : 'text-emerald-600'}`}>
				<div className="flex items-center space-x-2">
					<div className="w-1 h-3 bg-current animate-pulse" />
					<span>[ FETCHING_PHONE_RECORDS... ]</span>
				</div>
			</div>
		);
	}

	return (
		<div className="space-y-4 px-2 md:px-0 pb-20">
			{/* Header */}
			<div className={`flex flex-col md:flex-row items-start justify-between md:items-center p-4 border rounded-none ${isDark ? 'bg-[#111] border-[#222]' : 'bg-white border-neutral-200'} gap-4`}>
				<div>
					<h1 className={`text-sm font-mono font-bold tracking-widest uppercase ${isDark ? 'text-neutral-200' : 'text-neutral-800'}`}>
						// SYS: CONTACT_NUMBERS
					</h1>
					<p className={`text-[10px] font-mono mt-0.5 uppercase tracking-wider ${isDark ? 'text-emerald-500/70' : 'text-emerald-600/70'}`}>
						{globalSearchTerm ? `MATCHING_RECORDS: ${sortedNumbers.length} / ${filteredNumbers.length}` : `TOTAL_RECORDS: ${filteredNumbers.length}`}
					</p>
				</div>
				<div className="flex items-center space-x-2 self-end md:self-auto flex-wrap">
					<button
						onClick={() => setShowHidden(!showHidden)}
						className={`flex items-center space-x-1.5 px-3 py-1.5 text-[10px] font-mono tracking-wider uppercase border transition-colors ${
							isDark 
								? showHidden ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400' : 'bg-[#0a0a0a] border-[#333] text-neutral-500 hover:text-white' 
								: showHidden ? 'bg-emerald-50 border-emerald-200 text-emerald-600' : 'bg-white border-neutral-300 text-neutral-600 hover:text-black'
						}`}
						title={showHidden ? 'HIDE_SECURE_RECORDS' : 'SHOW_SECURE_RECORDS'}
					>
						{showHidden ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
						<span>{showHidden ? 'SECURE_VISIBLE' : 'SECURE_HIDDEN'}</span>
					</button>
					<button
						onClick={() => {
							setEditingNumber(null);
							setFormData({ pn_personId: '', pn_number: '', pn_status: 'active' });
							setShowModal(true);
						}}
						className={`flex items-center space-x-1.5 px-3 py-1.5 text-[10px] font-mono tracking-wider uppercase border text-emerald-500 hover:bg-emerald-500/10 transition-colors ${isDark ? 'border-[#333] bg-[#0a0a0a]' : 'border-neutral-300 bg-neutral-50'}`}
					>
						<Plus className="w-3 h-3" />
						<span>ADD_RECORD</span>
					</button>
				</div>
			</div>

			<div className={`border p-4 ${isDark ? 'bg-[#111] border-[#222]' : 'bg-white border-neutral-200'}`}>
				<h2 className={`text-[10px] font-mono tracking-widest uppercase mb-4 pb-2 border-b ${isDark ? 'text-emerald-500 border-[#222]' : 'text-emerald-700 border-neutral-200'}`}>
					// DATA_TABLE: NUMBERS
				</h2>
				<div className={`border overflow-hidden ${isDark ? 'border-[#222]' : 'border-neutral-200'}`}>
					<div className="overflow-x-auto">
						<table className="w-full text-[10px] font-mono whitespace-nowrap">
							<thead className={isDark ? 'bg-[#0a0a0a] border-b border-[#222]' : 'bg-neutral-50 border-b border-neutral-200'}>
								<tr>
									<th 
										className={`px-4 py-2 text-left font-normal uppercase cursor-pointer select-none transition-colors ${isDark ? 'text-neutral-500 hover:bg-[#1a1a1a]' : 'text-neutral-400 hover:bg-neutral-100'}`}
										onClick={() => handleSort('pn_number')}
									>
										<div className="flex items-center">
											IDENTIFIER_STRING
											<SortIcon field="pn_number" />
										</div>
									</th>
									<th 
										className={`px-4 py-2 text-left font-normal uppercase cursor-pointer select-none transition-colors ${isDark ? 'text-neutral-500 hover:bg-[#1a1a1a]' : 'text-neutral-400 hover:bg-neutral-100'}`}
										onClick={() => handleSort('person_name')}
									>
										<div className="flex items-center">
											LINKED_ENTITY
											<SortIcon field="person_name" />
										</div>
									</th>
									<th 
										className={`px-4 py-2 text-left font-normal uppercase cursor-pointer select-none transition-colors ${isDark ? 'text-neutral-500 hover:bg-[#1a1a1a]' : 'text-neutral-400 hover:bg-neutral-100'}`}
										onClick={() => handleSort('pn_status')}
									>
										<div className="flex items-center">
											STATUS_CODE
											<SortIcon field="pn_status" />
										</div>
									</th>
									<th className={`px-4 py-2 text-right font-normal uppercase ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>
										ACTIONS
									</th>
								</tr>
							</thead>
							<tbody className={`divide-y ${isDark ? 'divide-[#222]' : 'divide-neutral-200'}`}>
								{sortedNumbers.length > 0 ? (
									sortedNumbers.map((number) => (
										<tr key={number.pn_id} className={`hover:bg-opacity-50 transition-colors ${showHidden && number.person_hideHome === '1' ? (isDark ? 'hover:bg-amber-900/10 bg-amber-900/5 border-l-2 border-amber-500/30' : 'hover:bg-amber-50/50 bg-amber-50/30 border-l-2 border-amber-400/40') : (isDark ? 'hover:bg-[#1a1a1a] bg-[#111]' : 'hover:bg-neutral-50 bg-white')}`}>
											<td className={`px-4 py-2 uppercase ${isDark ? 'text-emerald-500' : 'text-emerald-600'}`}>
												<div className="flex items-center gap-2">
													{formatPhoneNumber(number.pn_number)}
													{showHidden && number.person_hideHome === '1' && (
														<span className={`text-[8px] font-mono px-1 py-0.5 border ${isDark ? 'border-amber-500/40 text-amber-400 bg-amber-500/10' : 'border-amber-400 text-amber-600 bg-amber-50'}`}>
															SECURE
														</span>
													)}
												</div>
											</td>
											<td className={`px-4 py-2`}>
												{number.person_name && number.person_surname ? (
													<div className="flex items-center space-x-2">
														{number.person_avatar ? (
															<img
																src={getStaticFileUrl(number.person_avatar) || ''}
																alt="AVT"
																className={`w-6 h-6 object-cover border cursor-pointer hover:opacity-80 transition-all ${isDark ? 'border-[#333]' : 'border-neutral-300'}`}
															/>
														) : (
															<div className={`w-6 h-6 flex items-center justify-center font-mono text-[8px] border ${isDark ? 'bg-[#1a1a1a] text-neutral-500 border-[#333]' : 'bg-neutral-100 text-neutral-500 border-neutral-300'}`}>
																{(number.person_name || '').charAt(0)}{(number.person_surname || '').charAt(0)}
															</div>
														)}
														<button
															onClick={() => navigate(`/stalker/persons/${number.pn_personId}`)}
															className={`uppercase font-bold tracking-wider hover:underline ${isDark ? 'text-neutral-300' : 'text-neutral-700'}`}
														>
															{number.person_name} {number.person_surname}
														</button>
													</div>
												) : (
													<span className={`uppercase tracking-wider ${isDark ? 'text-neutral-600' : 'text-neutral-400'}`}>[ UNKNOWN_ENTITY ]</span>
												)}
											</td>
											<td className="px-4 py-2">
												<span className={`font-bold uppercase tracking-wider text-[10px] font-mono ${
													(number.pn_status || '').toLowerCase() === 'active'
														? (isDark ? 'text-emerald-400' : 'text-emerald-600')
														: (isDark ? 'text-red-400' : 'text-red-600')
												}`}>
													{STATUS_MAP[number.pn_status as keyof typeof STATUS_MAP] || number.pn_status}
												</span>
											</td>
											<td className="px-4 py-2 text-right">
												<div className="flex items-center justify-end space-x-1">
													<button
														onClick={() => {
															setEditingNumber(number);
															setFormData({
																pn_personId: String(number.pn_personId),
																pn_number: number.pn_number,
																pn_status: number.pn_status
															});
															setShowModal(true);
														}}
														className={`p-1.5 border transition-colors ${isDark ? 'bg-black/80 border-[#333] hover:bg-[#222] text-neutral-400 hover:text-white' : 'bg-white/90 border-neutral-300 hover:bg-neutral-100 text-neutral-600 hover:text-black'}`}
														title="MODIFY"
													>
														<Edit className="w-3.5 h-3.5" />
													</button>
													<button
														onClick={() => handleDelete(number.pn_id)}
														className={`p-1.5 border transition-colors ${isDark ? 'bg-black/80 border-[#333] hover:bg-red-900/50 text-red-500 hover:text-red-400' : 'bg-white/90 border-neutral-300 hover:bg-red-50 text-red-500 hover:text-red-600'}`}
														title="PURGE"
													>
														<Trash2 className="w-3.5 h-3.5" />
													</button>
												</div>
											</td>
										</tr>
									))
								) : (
									<tr>
										<td colSpan={4} className={`px-4 py-8 text-center uppercase tracking-widest ${isDark ? 'text-neutral-600' : 'text-neutral-400'}`}>
											[ NO_RECORDS_FOUND ]
										</td>
									</tr>
								)}
							</tbody>
						</table>
					</div>
				</div>
			</div>

			{/* Modal */}
			{showModal && createPortal(
				<div
					className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[1005] p-4"
					onClick={() => setShowModal(false)}
				>
					<div
						className={`border p-6 w-full max-w-md ${isDark ? 'bg-[#111] border-[#333]' : 'bg-white border-neutral-300'}`}
						onClick={(e) => e.stopPropagation()}
					>
						<div className={`flex justify-between items-center mb-6 pb-2 border-b ${isDark ? 'border-[#333]' : 'border-neutral-200'}`}>
							<h2 className={`text-[10px] font-mono tracking-widest uppercase ${isDark ? 'text-emerald-500' : 'text-emerald-600'}`}>
								{editingNumber ? '// EDIT_RECORD' : '// NEW_RECORD'}
							</h2>
							<button
								onClick={() => {
									setShowModal(false);
									setEditingNumber(null);
									setFormData({ pn_personId: '', pn_number: '', pn_status: 'active' });
								}}
								className={`p-1 border transition-colors ${isDark ? 'border-[#333] hover:bg-[#222] text-neutral-400 hover:text-white' : 'border-neutral-200 hover:bg-neutral-100 text-neutral-500 hover:text-black'}`}
							>
								<X className="w-4 h-4" />
							</button>
						</div>
						<form onSubmit={handleSubmit} className="space-y-4 font-mono text-[10px] uppercase tracking-wider">
							<div>
								<label className={`block mb-1.5 ${isDark ? 'text-neutral-400' : 'text-neutral-600'}`}>
									LINKED_ENTITY <span className="text-emerald-500">*</span>
								</label>
								<select
									value={formData.pn_personId}
									onChange={(e) => setFormData({ ...formData, pn_personId: e.target.value })}
									className={`w-full px-3 py-2 border outline-none transition-colors ${isDark ? 'bg-[#0a0a0a] border-[#333] focus:border-emerald-500/50 text-neutral-200' : 'bg-neutral-50 border-neutral-300 focus:border-emerald-500/50 text-neutral-800'}`}
									required
									disabled={!!editingNumber}
								>
									<option value="">[ SELECT_ENTITY ]</option>
									{persons.map((person) => (
										<option key={person.person_id} value={person.person_id}>
											{person.person_name} {person.person_surname}
										</option>
									))}
								</select>
							</div>
							<div>
								<label className={`block mb-1.5 ${isDark ? 'text-neutral-400' : 'text-neutral-600'}`}>
									IDENTIFIER_STRING <span className="text-emerald-500">*</span>
								</label>
								<input
									type="text"
									value={formData.pn_number}
									onChange={(e) => setFormData({ ...formData, pn_number: e.target.value })}
									className={`w-full px-3 py-2 border outline-none transition-colors placeholder:text-neutral-600 ${isDark ? 'bg-[#0a0a0a] border-[#333] focus:border-emerald-500/50 text-neutral-200' : 'bg-neutral-50 border-neutral-300 focus:border-emerald-500/50 text-neutral-800'}`}
									placeholder="e.g. 0555 555 55 55"
									required
								/>
							</div>
							<div>
								<label className={`block mb-1.5 ${isDark ? 'text-neutral-400' : 'text-neutral-600'}`}>STATUS_CODE</label>
								<select
									value={formData.pn_status}
									onChange={(e) => setFormData({ ...formData, pn_status: e.target.value })}
									className={`w-full px-3 py-2 border outline-none transition-colors ${isDark ? 'bg-[#0a0a0a] border-[#333] focus:border-emerald-500/50 text-neutral-200' : 'bg-neutral-50 border-neutral-300 focus:border-emerald-500/50 text-neutral-800'}`}
								>
									<option value="active">ACTIVE</option>
									<option value="notactive">NOT_ACTIVE</option>
								</select>
							</div>
							<div className="flex justify-end space-x-2 pt-4">
								<button
									type="button"
									onClick={() => {
										setShowModal(false);
										setEditingNumber(null);
										setFormData({ pn_personId: '', pn_number: '', pn_status: 'active' });
									}}
									className={`px-4 py-2 border transition-colors ${isDark ? 'border-[#333] hover:bg-[#222] text-neutral-400 hover:text-white' : 'border-neutral-300 hover:bg-neutral-100 text-neutral-600 hover:text-black'}`}
								>
									CANCEL
								</button>
								<button
									type="submit"
									className={`px-4 py-2 border transition-colors ${isDark ? 'bg-[#0a0a0a] border-emerald-500/30 text-emerald-500 hover:bg-emerald-500/10' : 'bg-emerald-50 border-emerald-200 text-emerald-600 hover:bg-emerald-100'}`}
								>
									{editingNumber ? 'EXECUTE_UPDATE' : 'EXECUTE_CREATE'}
								</button>
							</div>
						</form>
					</div>
				</div>,
				document.body
			)}
		</div>
	);
};

export default Numbers;

